import { Autocomplete, Box, CircularProgress, TextField, Typography } from "@mui/material"
import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { searchDoctors, getDoctorsByClinic } from "../../apis/doctorSlice";
import { useDispatch } from "react-redux";

const getSpecialityName = (item) => {
  if (!item?.docSpeciality) return "";
  if (typeof item.docSpeciality === "object" && item.docSpeciality?.name) {
    return item.docSpeciality.name;
  }
  return "";
};

const mapDoctorOption = (item, showSpecialityInLabel) => {
  const specialityName = getSpecialityName(item);
  const base = {
    value: item._id,
    name: item.name,
    _id: item._id,
    docSpeciality: item.docSpeciality,
    specialityName,
  };
  if (showSpecialityInLabel && specialityName) {
    return {
      ...base,
      label: `${item.name} — ${specialityName}`,
    };
  }
  return {
    ...base,
    label: item.name,
  };
};

const SearchDoctor = ({
  open,
  setData,
  data,
  variant,
  label,
  name,
  size,
  clinicId,
  disabled,
  required = "true",
  /** When true, dropdown shows doctor name with speciality (only where passed). */
  showSpecialityInLabel = false,
}) => {
    const dispatch = useDispatch();

    const [docOptions, setDocOptions] = useState([]);
    const [docInputValue, setDocInputValue] = useState("");
    const [loading, setLoading] = useState(false);

    const doctorFetchOptions = useMemo(
        () =>
            _.debounce(async (query) => {
                setLoading(true);
                try {
                    const params = { search: query };
                    if (clinicId) {
                        params.clinicId = clinicId;
                    }
                    
                    const response = clinicId 
                        ? await dispatch(getDoctorsByClinic(params))
                        : await dispatch(searchDoctors(params));

                    setDocOptions(
                        response?.payload?.data?.map((item) =>
                            mapDoctorOption(item, showSpecialityInLabel),
                        ) || [],
                    );
                } catch (error) {
                    console.error("Error fetching data:", error);
                } finally {
                    setLoading(false);
                }
            }, 1000),
        [dispatch, clinicId, showSpecialityInLabel]
    );

    useEffect(() => {
        if (open) {
            doctorFetchOptions(docInputValue);
        }
    }, [open, docInputValue, clinicId]);

    const handleChange = (e, newValue) => {
        setData((prev) => ({
            ...prev,
            [name]: newValue || null, // set to null if cleared
        }));
    };

    return (
        <Autocomplete
            options={docOptions}
            getOptionLabel={(option) =>
                typeof option === "string" ? option : option.label || ""
            }
            isOptionEqualToValue={(option, value) =>
                (option?._id || option?.value) === (value?._id || value?.value)
            }
            value={data?.[name] || data?.doctor || null}
            loading={loading}
            size={size || "medium"}
            onChange={handleChange}
            disabled={disabled}
            onInputChange={(event, newInputValue) => {
                setDocInputValue(newInputValue);
            }}
            renderOption={
                showSpecialityInLabel
                    ? (props, option) => (
                          <li {...props} key={option.value}>
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.15, py: 0.15 }}>
                                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--color-heading)" }}>
                                      {option.name || option.label}
                                  </Typography>
                                  {!!option.specialityName && (
                                      <Typography sx={{ fontSize: "0.72rem", color: "var(--color-text-muted)", fontWeight: 600 }}>
                                          {option.specialityName}
                                      </Typography>
                                  )}
                              </Box>
                          </li>
                      )
                    : (props, option) => (
                          <li {...props} key={option.value}>
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.15, py: 0.15 }}>
                                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--color-heading)" }}>
                                      {option.name || option.label}
                                  </Typography>
                                  {!!option.specialityName && (
                                      <Typography sx={{ fontSize: "0.72rem", color: "var(--color-text-muted)", fontWeight: 600 }}>
                                          {option.specialityName}
                                      </Typography>
                                  )}
                              </Box>
                          </li>
                      )
            }
            renderInput={(params) => (
                <TextField
                    required={required}
                    {...params}
                    label={label || "Select Doctors"}
                    variant={variant}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading && <CircularProgress color="inherit" size={20} />}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: "10px",
                        border: "1px solid var(--color-border-soft)",
                        boxShadow: "0 10px 28px rgba(16,52,92,0.12)",
                        "& .MuiAutocomplete-option": {
                            minHeight: 34,
                            py: 0.4,
                        },
                    },
                },
            }}
        />
    );
};


export default SearchDoctor